import React from "react";
import {
  TableRow,
  TableCell,
  Button,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import PricelistSelect from "./pricing/PricelistSelect";
import UserGroupDropdown from "./UserGroupDropdown";

/**
 * UserRow component - Reusable table row for displaying user information
 *
 * @param {Object} props - Component props
 * @param {Object} props.user - User object with all user data
 * @param {boolean} props.isSuperAdmin - Controls which fields are visible
 * @param {Function} props.onPricelistUpdate - Callback for pricelist changes
 * @param {Function} props.onViewEdit - Callback for view/edit action
 * @param {Function} [props.onProviderToggle] - Callback for provider toggle (super admin only)
 * @param {Function} [props.onDelete] - Callback for delete action (super admin only)
 * @param {string} [props.currentUserID] - Current user ID for group dropdown
 * @param {Function} [props.onUserUpdated] - Callback for user updates
 * @param {Function} [props.onGroupUpdate] - Callback for group updates
 * @param {number} [props.customerCount] - Number of customers for this provider
 */
const UserRow = ({
  user,
  isSuperAdmin,
  onPricelistUpdate,
  onViewEdit,
  onProviderToggle,
  onDelete,
  currentUserID,
  onUserUpdated,
  onGroupUpdate,
  customerCount = 0,
}) => {
  return (
    <TableRow
      key={user.email}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      {/* Email - Common to both */}
      <TableCell>{user.email}</TableCell>

      {/* Username - Common to both */}
      <TableCell component="th" scope="row">
        {user.username}
      </TableCell>

      {/* Active Pricelist - Common to both */}
      <TableCell align="right">
        <PricelistSelect
          pricelistID={user.activePricelistID}
          userID={user.id}
          onPricelistUpdate={onPricelistUpdate}
        />
      </TableCell>

      {/* Conditional columns based on isSuperAdmin */}
      {isSuperAdmin ? (
        <>
          {/* Provider Name - Super Admin Only */}
          <TableCell align="right">
            {user.activePricelist?.provider?.username || "N/A"}
          </TableCell>

          {/* Searches Remaining - Common to both */}
          <TableCell align="right">{user.searchesRemaining || "N/A"}</TableCell>

          {/* Subscription - Super Admin Only */}
          <TableCell align="right">
            <Chip
              label={user.subscriptionActive ? "Active" : "Inactive"}
              color={user.subscriptionActive ? "success" : "error"}
            />
          </TableCell>

          {/* Provider Toggle - Super Admin Only */}
          <TableCell
            align="right"
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={user.isProvider || false}
                  onChange={() => onProviderToggle && onProviderToggle(user)}
                />
              }
              label={user.isProvider ? "Yes" : "No"}
            />
            {/* Customer's Personal Offset Badge */}
            <Chip
              label={
                user.priceOffsetValue && user.priceOffsetValue !== 0
                  ? user.priceOffsetType === "percentage"
                    ? `${user.priceOffsetValue > 0 ? "+" : ""}${
                        user.priceOffsetValue
                      }%`
                    : `$${user.priceOffsetValue > 0 ? "+" : ""}${Math.abs(
                        user.priceOffsetValue,
                      ).toFixed(2)}`
                  : "No personal offset"
              }
              size="small"
              color={
                user.priceOffsetValue && user.priceOffsetValue > 0
                  ? "warning"
                  : user.priceOffsetValue && user.priceOffsetValue < 0
                    ? "success"
                    : "default"
              }
              sx={{ fontWeight: "bold" }}
            />
            {/* Provider's Personal Offset Badge */}
            <Chip
              label={
                user.providerOffsetValue && user.providerOffsetValue !== 0
                  ? user.providerOffsetType === "percentage"
                    ? `${user.providerOffsetValue > 0 ? "+" : ""}${
                        user.providerOffsetValue
                      }%`
                    : `$${user.providerOffsetValue > 0 ? "+" : ""}${Math.abs(
                        user.providerOffsetValue,
                      ).toFixed(2)}`
                  : "No personal offset"
              }
              size="small"
              color={
                user.providerOffsetValue && user.providerOffsetValue > 0
                  ? "warning"
                  : user.providerOffsetValue && user.providerOffsetValue < 0
                    ? "success"
                    : "default"
              }
              sx={{ fontWeight: "bold" }}
            />
            {customerCount > 0 && (
              <Chip
                label={`${customerCount} customers`}
                size="small"
                color="primary"
              />
            )}
          </TableCell>

          {/* Created Date - Super Admin Only */}
          <TableCell align="right">
            {user.createdAt ? new Date(user.createdAt).toLocaleString() : "N/A"}
          </TableCell>
        </>
      ) : (
        <>
          {/* User Groups - Regular User Only */}
          <TableCell align="right">
            <UserGroupDropdown
              userId={user.id}
              currentGroups={user.userGroupID}
              onGroupUpdate={onGroupUpdate}
              onUserUpdated={onUserUpdated}
              currentUserID={currentUserID}
            />
          </TableCell>

          {/* Searches Remaining - Common to both */}
          <TableCell align="right">{user.searchesRemaining || "100"}</TableCell>

          {/* Price Offset - Regular User Only */}
          <TableCell align="right">
            {user.priceOffsetValue
              ? user.priceOffsetType === "percentage"
                ? `${user.priceOffsetValue > 0 ? "+" : ""}${
                    user.priceOffsetValue
                  }%`
                : `${user.priceOffsetValue > 0 ? "+" : ""}$${Math.abs(
                    user.priceOffsetValue,
                  ).toFixed(2)}`
              : "None"}
          </TableCell>

          {/* User ID - Regular User Only */}
          <TableCell align="right">{user.id}</TableCell>
        </>
      )}

      {/* Actions - Common to both */}
      <TableCell align="right">
        <Button onClick={() => onViewEdit(user)}>View/Edit</Button>
        {isSuperAdmin && onDelete && (
          <Button color="error" onClick={() => onDelete(user)}>
            Delete
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default UserRow;
